export const translations = {
  en: {
    nav: {
      about: "About",
      projects: "Projects",
      experience: "Experience",
      skills: "Skills",
      contact: "Contact",
      viewCV: "View CV"
    },
    hero: {
      tagline: "Data Scientist · AI/ML Automation & Test Engineer",
      availability: "Open to opportunities",
      title: "Building Intelligent",
      titleHighlight: "Solutions",
      claims: [
        "I build ML pipelines that ship to production",
        "I automate what slows QA and ML teams down",
        "I turn raw data into tools people actually use",
        "I test, evaluate, and deploy AI systems at scale",
      ],
      proofHook: "20+ models deployed · 500+ active users · 40%+ cost cuts shipped",
      description: "Specializing in AI/ML, Data Science, and full-stack development. Creating scalable solutions that drive business value and innovation.",
      viewProjects: "View Projects",
      getInTouch: "Get In Touch",
      stats: {
        efficiencyGain: "Efficiency Gain",
        efficiencyContext: "QA automation · Edpulse",
        activeUsers: "Active Users",
        activeUsersContext: "Edpulse SaaS platform",
        mlAccuracy: "ML Accuracy",
        mlAccuracyContext: "Financial & legal models · Arhs",
        costReduction: "Cost Reduction",
        costReductionContext: "Process automation · Edpulse"
      }
    },
    about: {
      title: "About Me",
      lead: "I help teams ship reliable AI, from ML pipelines and chatbots to production test automation.",
      bullets: [
        "Built LangChain/LangGraph chatbots integrating OpenAI, Claude, and Gemini",
        "Deployed 20+ ML models in production with MLOps at 85%+ accuracy",
        "Cut manual work 60% and operational costs 40%+ through automation",
        "Shipped full-stack platforms serving 500+ active users"
      ],
      mastersDegrees: "Master's Degrees",
      mlsd: "MLSD - Université Paris-Cité",
      bigData: "Big Data & Cloud - Université Ibn Tofail",
      yearsExperience: "4+ Years",
      professionalExperience: "Professional Experience",
      modelsDeployed: "20+ Models",
      deployedInProduction: "Deployed in Production"
    },
    projects: {
      title: "Featured Projects",
      subtitle: "A selection of projects showcasing my expertise in AI/ML, Data Science, and full-stack development",
      keyHighlights: "Key Highlights",
      problem: "Problem",
      built: "What I built",
      outcome: "Outcome",
      items: [
        {
          title: "Professional Web Platforms",
          category: "Web Development • UI/UX",
          problem: "Two businesses needed production-ready sites with booking, payments, and modern UX, not WordPress templates.",
          built: "Full-stack rental and dental clinic platforms with payment integration, calendar booking, and responsive UI.",
          outcome: "Two live production sites with < 1.5s load times, 98/100 mobile scores, and integrated payment flows.",
          metrics: [
            { label: "Page Load", value: "< 1.5s" },
            { label: "Mobile Score", value: "98/100" },
            { label: "Conversion Rate", value: "12%" }
          ],
          highlights: [
            "Fully responsive mobile-first design",
            "Integrated payment and booking systems",
            "SEO optimized with high performance scores",
            "Modern UI/UX with smooth animations"
          ],
          proof: []
        },
        {
          title: "AI-Powered Educational Chatbot",
          category: "AI/ML • Full-Stack",
          problem: "Edpulse needed a tutoring system that could route across multiple AI providers without vendor lock-in.",
          built: "LangChain/LangGraph chatbot with OpenAI, Claude, and Gemini. Microservices on Railway with CI/CD.",
          outcome: "< 2s response time, 99.9% uptime, and 4.8/5 user satisfaction across 500+ learners.",
          metrics: [
            { label: "Response Time", value: "< 2s" },
            { label: "Uptime", value: "99.9%" },
            { label: "User Satisfaction", value: "4.8/5" }
          ],
          highlights: [
            "Multi-API AI integration with intelligent fallback",
            "Real-time learning analytics dashboard",
            "Scalable microservices architecture",
            "Automated CI/CD with zero-downtime deployments"
          ],
          proof: [
            { type: "github", label: "GitHub", url: "https://github.com/NizarTazi/" },
            { type: "nda", label: "Production code · Private / NDA" }
          ]
        },
        {
          title: "SaaS Subscription Platform",
          category: "Full-Stack • E-Commerce",
          problem: "Manual subscription billing and user management couldn't scale past a few hundred users.",
          built: "React/Node.js SaaS with Stripe webhooks, SSO, role-based access, and a real-time analytics dashboard.",
          outcome: "500+ active users, 35% MRR growth, and 98.5% payment success rate.",
          metrics: [
            { label: "Active Users", value: "500+" },
            { label: "MRR Growth", value: "35%" },
            { label: "Payment Success", value: "98.5%" }
          ],
          highlights: [
            "Stripe payment integration with webhooks",
            "SSO authentication with multiple providers",
            "Real-time analytics and reporting",
            "Automated subscription lifecycle management"
          ],
          proof: [
            { type: "github", label: "GitHub", url: "https://github.com/NizarTazi/" },
            { type: "nda", label: "Client platform · Private / NDA" }
          ]
        },
        {
          title: "MLOps Production Pipeline",
          category: "Data Science • MLOps",
          problem: "Financial and legal teams needed repeatable model deployment, not one-off Jupyter notebooks.",
          built: "End-to-end MLOps with MLflow versioning, monitoring, A/B testing, and automated retraining on Azure.",
          outcome: "20+ models in production at 85%+ accuracy with 50% faster training cycles.",
          metrics: [
            { label: "Models Deployed", value: "20+" },
            { label: "Avg Accuracy", value: "85%+" },
            { label: "Training Time", value: "-50%" }
          ],
          highlights: [
            "Automated model versioning and tracking",
            "Real-time performance monitoring",
            "A/B testing framework",
            "Automated retraining pipelines"
          ],
          proof: [
            { type: "nda", label: "Enterprise deployment · Private / NDA" },
            { type: "link", label: "MLflow", url: "https://mlflow.org/" }
          ]
        }
      ]
    },
    experience: {
      title: "Professional Experience",
      current: "Current",
      items: [
        {
          title: "AI/ML Automation & Test Engineer",
          location: "Remote",
          period: "May 2026 - Present",
          achievements: [
            "Evaluate and validate AI/ML model outputs across end-to-end test cycles to ensure data quality and accuracy",
            "Build automation and data-filtering pipelines on Monday.com for AI/ML evaluation projects",
            "Coordinate cross-functional test teams (TTLs/DTs) as primary escalation point across concurrent cycles",
            "Track cycle KPIs and flag blockers to TPM/TSM to keep AI/ML evaluation on schedule"
          ]
        },
        {
          title: "Automation & Full-Stack Engineer / Data Analysis",
          location: "Quebec (Remote)",
          period: "Nov 2024 - May 2026",
          achievements: [
            "Reduced manual tasks by 60% through automation",
            "Built subscription system managing 500+ active users",
            "Created data pipelines processing 10+ data sources",
            "Implemented real-time monitoring on Railway"
          ]
        },
        {
          title: "Data Scientist",
          location: "Brussels, Belgium",
          period: "Jun 2023 - Jun 2024",
          achievements: [
            "Developed ML models with 85%+ accuracy",
            "Trained 15+ teams across EU member states",
            "Deployed 20+ models in production with MLflow",
            "Implemented MLOps best practices"
          ]
        },
        {
          title: "Data Scientist Intern",
          location: "Gentilly, Ile-de-France",
          period: "Apr 2022 - Nov 2022",
          achievements: [
            "Built predictive models for pharmaceutical market analysis",
            "Processed 100K+ data points for oncology/Dupixent",
            "Reduced reporting time by 50% (2 weeks to 1 week)",
            "Created Power BI dashboards used by 30+ analysts"
          ]
        }
      ]
    },
    skills: {
      title: "Technical Expertise",
      production: "Production",
      productionDesc: "Shipped in live systems",
      comfortable: "Comfortable",
      comfortableDesc: "Regular use across projects",
      exploring: "Exploring",
      exploringDesc: "Active learning & prototyping",
      tiers: {
        production: [
          { name: "LangChain", context: "Edpulse chatbot" },
          { name: "LangGraph", context: "Multi-API orchestration" },
          { name: "Python", context: "ML & automation" },
          { name: "React", context: "SaaS & web platforms" },
          { name: "Node.js", context: "Full-stack APIs" },
          { name: "MLflow", context: "20+ models deployed" },
          { name: "Stripe", context: "500+ user billing" },
          { name: "PostgreSQL", context: "Production databases" }
        ],
        comfortable: [
          { name: "OpenAI API", context: "Chatbot integrations" },
          { name: "Claude API", context: "Multi-model fallback" },
          { name: "TensorFlow", context: "Deep learning models" },
          { name: "Scikit-learn", context: "Classical ML" },
          { name: "Docker", context: "Containerized services" },
          { name: "Azure", context: "Cloud ML deployment" },
          { name: "Power BI", context: "Sanofi dashboards" },
          { name: "TypeScript", context: "Typed frontends" }
        ],
        exploring: [
          { name: "PyTorch", context: "Research prototypes" },
          { name: "Kubernetes", context: "Scaling orchestration" },
          { name: "Spark", context: "Large-scale ETL" },
          { name: "GraphQL", context: "API experimentation" }
        ]
      }
    },
    testimonial: {
      quote: "Nizar combines strong ML fundamentals with rare full-stack delivery skills. He automated critical workflows and shipped production tools our team relied on daily.",
      author: "Technical Lead",
      company: "Edpulse"
    },
    contact: {
      title: "Let's Work Together",
      subtitle: "I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.",
      email: "Email",
      phone: "Phone",
      location: "Location",
      copyEmail: "Click to copy",
      copied: "Copied!"
    },
    footer: {
      rights: "All rights reserved."
    }
  },
  fr: {
    nav: {
      about: "À propos",
      projects: "Projets",
      experience: "Expérience",
      skills: "Compétences",
      contact: "Contact",
      viewCV: "Voir le CV"
    },
    hero: {
      tagline: "Data Scientist · Ingénieur Automatisation & Test IA/ML",
      availability: "Ouvert aux opportunités",
      title: "Création de",
      titleHighlight: "Solutions Intelligentes",
      claims: [
        "Je construis des pipelines ML qui passent en production",
        "J'automatise ce qui ralentit les équipes QA et ML",
        "Je transforme les données brutes en outils utiles",
        "Je teste, évalue et déploie des systèmes IA à l'échelle",
      ],
      proofHook: "20+ modèles déployés · 500+ utilisateurs actifs · 40%+ de réduction des coûts",
      description: "Spécialisé en IA/ML, Data Science et développement full-stack. Je crée des solutions évolutives qui apportent une valeur commerciale et favorisent l'innovation.",
      viewProjects: "Voir les Projets",
      getInTouch: "Me Contacter",
      stats: {
        efficiencyGain: "Gain d'Efficacité",
        efficiencyContext: "Automatisation QA · Edpulse",
        activeUsers: "Utilisateurs Actifs",
        activeUsersContext: "Plateforme SaaS Edpulse",
        mlAccuracy: "Précision ML",
        mlAccuracyContext: "Modèles finance & juridique · Arhs",
        costReduction: "Réduction des Coûts",
        costReductionContext: "Automatisation processus · Edpulse"
      }
    },
    about: {
      title: "À propos de moi",
      lead: "J'aide les équipes à déployer une IA fiable, des pipelines ML aux chatbots et à l'automatisation de tests en production.",
      bullets: [
        "Chatbots LangChain/LangGraph intégrant OpenAI, Claude et Gemini",
        "20+ modèles ML déployés en production avec MLOps à 85%+ de précision",
        "Réduction de 60% du travail manuel et de 40%+ des coûts opérationnels",
        "Plateformes full-stack pour 500+ utilisateurs actifs"
      ],
      mastersDegrees: "Diplômes de Master",
      mlsd: "MLSD - Université Paris-Cité",
      bigData: "Big Data & Cloud - Université Ibn Tofail",
      yearsExperience: "4+ Ans",
      professionalExperience: "d'Expérience Professionnelle",
      modelsDeployed: "20+ Modèles",
      deployedInProduction: "Déployés en Production"
    },
    projects: {
      title: "Projets Phares",
      subtitle: "Une sélection de projets mettant en valeur mon expertise en IA/ML, Data Science et développement full-stack",
      keyHighlights: "Points Clés",
      problem: "Problème",
      built: "Ce que j'ai construit",
      outcome: "Résultat",
      items: [
        {
          title: "Plateformes Web Professionnelles",
          category: "Développement Web • UI/UX",
          problem: "Deux entreprises avaient besoin de sites prêts pour la production avec réservation, paiements et UX moderne.",
          built: "Plateformes full-stack de location et clinique dentaire avec paiement, calendrier et UI responsive.",
          outcome: "Deux sites en production avec chargement < 1,5s, score mobile 98/100 et flux de paiement intégrés.",
          metrics: [
            { label: "Chargement Page", value: "< 1.5s" },
            { label: "Score Mobile", value: "98/100" },
            { label: "Taux Conversion", value: "12%" }
          ],
          highlights: [
            "Design mobile-first entièrement responsive",
            "Systèmes de paiement et de réservation intégrés",
            "Optimisé SEO avec scores de performance élevés",
            "UI/UX moderne avec animations fluides"
          ],
          proof: []
        },
        {
          title: "Chatbot Éducatif Alimenté par l'IA",
          category: "IA/ML • Full-Stack",
          problem: "Edpulse avait besoin d'un système de tutorat capable de router entre plusieurs fournisseurs IA sans vendor lock-in.",
          built: "Chatbot LangChain/LangGraph avec OpenAI, Claude et Gemini. Microservices sur Railway avec CI/CD.",
          outcome: "Temps de réponse < 2s, disponibilité 99,9% et satisfaction utilisateur 4,8/5 sur 500+ apprenants.",
          metrics: [
            { label: "Temps de Réponse", value: "< 2s" },
            { label: "Disponibilité", value: "99.9%" },
            { label: "Satisfaction Utilisateur", value: "4.8/5" }
          ],
          highlights: [
            "Intégration multi-API IA avec basculement intelligent",
            "Tableau de bord d'analyse d'apprentissage en temps réel",
            "Architecture microservices évolutive",
            "CI/CD automatisé avec déploiements sans interruption"
          ],
          proof: [
            { type: "github", label: "GitHub", url: "https://github.com/NizarTazi/" },
            { type: "nda", label: "Code production · Privé / NDA" }
          ]
        },
        {
          title: "Plateforme SaaS d'Abonnement",
          category: "Full-Stack • E-Commerce",
          problem: "La facturation manuelle et la gestion des utilisateurs ne pouvaient pas scaler au-delà de quelques centaines d'utilisateurs.",
          built: "SaaS React/Node.js avec webhooks Stripe, SSO, contrôle d'accès par rôles et dashboard analytique temps réel.",
          outcome: "500+ utilisateurs actifs, croissance MRR de 35% et taux de succès paiement de 98,5%.",
          metrics: [
            { label: "Utilisateurs Actifs", value: "500+" },
            { label: "Croissance MRR", value: "35%" },
            { label: "Succès Paiement", value: "98.5%" }
          ],
          highlights: [
            "Intégration paiement Stripe avec webhooks",
            "Authentification SSO avec plusieurs fournisseurs",
            "Analyses et rapports en temps réel",
            "Gestion automatisée du cycle de vie des abonnements"
          ],
          proof: [
            { type: "github", label: "GitHub", url: "https://github.com/NizarTazi/" },
            { type: "nda", label: "Plateforme client · Privé / NDA" }
          ]
        },
        {
          title: "Pipeline MLOps de Production",
          category: "Data Science • MLOps",
          problem: "Les équipes finance et juridique avaient besoin d'un déploiement reproductible, pas de notebooks Jupyter ponctuels.",
          built: "MLOps de bout en bout avec versioning MLflow, monitoring, tests A/B et réentraînement automatisé sur Azure.",
          outcome: "20+ modèles en production à 85%+ de précision avec cycles d'entraînement 50% plus rapides.",
          metrics: [
            { label: "Modèles Déployés", value: "20+" },
            { label: "Précision Moyenne", value: "85%+" },
            { label: "Temps Formation", value: "-50%" }
          ],
          highlights: [
            "Versioning et suivi automatisés des modèles",
            "Surveillance des performances en temps réel",
            "Framework de tests A/B",
            "Pipelines de réentraînement automatisés"
          ],
          proof: [
            { type: "nda", label: "Déploiement entreprise · Privé / NDA" },
            { type: "link", label: "MLflow", url: "https://mlflow.org/" }
          ]
        }
      ]
    },
    experience: {
      title: "Expérience Professionnelle",
      current: "Actuel",
      items: [
        {
          title: "Ingénieur Automatisation & Test IA/ML",
          location: "Télétravail",
          period: "Mai 2026 - Présent",
          achievements: [
            "Évaluation et validation des sorties de modèles IA/ML sur des cycles de test end-to-end pour garantir qualité et précision des données",
            "Construction de pipelines d'automatisation et de filtrage de données sur Monday.com pour des projets d'évaluation IA/ML",
            "Coordination d'équipes de test transverses (TTLs/DTs) en tant que point d'escalade principal sur plusieurs cycles simultanés",
            "Suivi des KPI de cycle et remontée des blocages aux TPM/TSM pour respecter le planning d'évaluation IA/ML"
          ]
        },
        {
          title: "Ingénieur Full-Stack & Automatisation / Analyse de Données",
          location: "Québec (Télétravail)",
          period: "Nov 2024 - Mai 2026",
          achievements: [
            "Réduction des tâches manuelles de 60% grâce à l'automatisation",
            "Construction d'un système d'abonnement gérant 500+ utilisateurs actifs",
            "Création de pipelines de données traitant 10+ sources de données",
            "Mise en œuvre de surveillance en temps réel sur Railway"
          ]
        },
        {
          title: "Data Scientist",
          location: "Bruxelles, Belgique",
          period: "Juin 2023 - Juin 2024",
          achievements: [
            "Développement de modèles ML avec une précision de 85%+",
            "Formation de 15+ équipes à travers les états membres de l'UE",
            "Déploiement de 20+ modèles en production avec MLflow",
            "Mise en œuvre des meilleures pratiques MLOps"
          ]
        },
        {
          title: "Stagiaire Data Scientist",
          location: "Gentilly, Île-de-France",
          period: "Avr 2022 - Nov 2022",
          achievements: [
            "Développement de modèles prédictifs pour l'analyse du marché pharmaceutique",
            "Traitement de 100K+ points de données pour oncologie/Dupixent",
            "Réduction du temps de reporting de 50% (de 2 semaines à 1 semaine)",
            "Création de tableaux de bord Power BI utilisés par 30+ analystes"
          ]
        }
      ]
    },
    skills: {
      title: "Expertise Technique",
      production: "Production",
      productionDesc: "Déployé en systèmes live",
      comfortable: "Maîtrisé",
      comfortableDesc: "Usage régulier en projets",
      exploring: "En exploration",
      exploringDesc: "Apprentissage & prototypage actifs",
      tiers: {
        production: [
          { name: "LangChain", context: "Chatbot Edpulse" },
          { name: "LangGraph", context: "Orchestration multi-API" },
          { name: "Python", context: "ML & automatisation" },
          { name: "React", context: "SaaS & plateformes web" },
          { name: "Node.js", context: "APIs full-stack" },
          { name: "MLflow", context: "20+ modèles déployés" },
          { name: "Stripe", context: "Facturation 500+ users" },
          { name: "PostgreSQL", context: "Bases de production" }
        ],
        comfortable: [
          { name: "OpenAI API", context: "Intégrations chatbot" },
          { name: "Claude API", context: "Fallback multi-modèle" },
          { name: "TensorFlow", context: "Modèles deep learning" },
          { name: "Scikit-learn", context: "ML classique" },
          { name: "Docker", context: "Services containerisés" },
          { name: "Azure", context: "Déploiement ML cloud" },
          { name: "Power BI", context: "Dashboards Sanofi" },
          { name: "TypeScript", context: "Frontends typés" }
        ],
        exploring: [
          { name: "PyTorch", context: "Prototypes recherche" },
          { name: "Kubernetes", context: "Orchestration scale" },
          { name: "Spark", context: "ETL large échelle" },
          { name: "GraphQL", context: "Expérimentation API" }
        ]
      }
    },
    testimonial: {
      quote: "Nizar combine de solides fondamentaux ML avec de rares compétences full-stack. Il a automatisé des workflows critiques et livré des outils de production dont notre équipe dépendait au quotidien.",
      author: "Technical Lead",
      company: "Edpulse"
    },
    contact: {
      title: "Collaborons Ensemble",
      subtitle: "Disponible pour discuter de nouveaux projets, collaborations stratégiques et opportunités professionnelles.",
      email: "Email",
      phone: "Téléphone",
      location: "Localisation",
      copyEmail: "Cliquer pour copier",
      copied: "Copié !"
    },
    footer: {
      rights: "Tous droits réservés."
    }
  }
};

// CV Translations
export const cvTranslations = {
  en: {
    title: "Data Scientist · AI/ML Automation & Test Engineer",
    profile: "Professional Profile",
    profileText1: "Data Scientist with 4+ years building and deploying ML models, from predictive analytics (Python, Scikit-learn) to generative AI (LangChain/LangGraph, OpenAI, Claude, Gemini). Experienced in MLOps (MLflow, Dataiku), AI/ML model evaluation, and large-scale data pipelines. Proven impact: 20+ models in production at 85%+ accuracy, 100K+ data points processed, and end-to-end validation of production AI systems.",
    experience: "Professional Experience",
    education: "Education",
    projects: "Professional Projects",
    skills: "Technical Skills",
    languages: "Languages",
    downloadPDF: "Download PDF",
    portfolio: "Portfolio",
    current: "CURRENT",
    experiences: [
      {
        title: "AI/ML Automation & Test Engineer",
        company: "Applause",
        location: "Remote",
        period: "May 2026 – Present",
        points: [
          "Evaluate and validate AI/ML model outputs across end-to-end test cycles, ensuring data quality and accuracy for production AI systems",
          "Build hands-on automation on Monday.com for AI/ML projects: data-filtering pipelines, board configuration and workflow tooling",
          "Lead and coordinate cross-functional test teams (TTLs/DTs) as primary escalation point, balancing workload across concurrent cycles",
          "Monitor cycle KPIs and flag blocking issues to TPM/TSM to keep AI/ML evaluation on schedule"
        ]
      },
      {
        title: "Automation & Full-Stack Engineer / Data Analysis",
        company: "Edpulse",
        location: "Quebec (Remote)",
        period: "November 2024 – May 2026",
        points: [
          "Development of LangChain/LangGraph chatbots integrating AI APIs (OpenAI, Claude, Gemini) with microservices architecture",
          "Built a full-stack subscription system (React/Node.js) with Stripe, SSO and automated CI/CD",
          "Process automation (-60% manual tasks), plus KPI dashboards and ETL pipelines across 10+ data sources",
          "Real-time monitoring on Railway and RESTful APIs for third-party integration"
        ]
      },
        {
          title: "Data Scientist",
          company: "Arhs Development",
          location: "Brussels, Belgium",
          period: "June 2023 – June 2024",
        points: [
          "Development of Python/Scikit-learn ML models for financial and legal applications with 85%+ average accuracy",
          "Training of 15+ member state teams on Dataiku: preprocessing, modeling and deployment of data pipelines",
          "MLOps implementation with MLflow for versioning and monitoring of 20+ models in production"
        ]
      },
        {
          title: "Data Scientist Intern",
          company: "Sanofi",
          location: "Gentilly, Ile-de-France",
          period: "April 2022 – November 2022",
        points: [
          "Development of ML predictive models for pharmaceutical market analysis (Oncology/Dupixent) processing 100K+ data points",
          "Automation of Python/SQL ETL pipelines, reducing reporting time by 50% (from 2 weeks to 1 week)",
          "Creation of Power BI dashboards for market performance tracking and decision support used by 30+ analysts"
        ]
      },
      {
        title: "Freelance Data Scientist",
        company: "Khamsat",
        location: "Freelance",
        period: "April 2020 – June 2020",
        points: [
          "Python web scraping tool & desktop GUI for automated e-commerce data extraction (10K+ products/day)",
          "Django web app with an ML-based medical prediction system, secure authentication and RESTful APIs"
        ]
      }
    ],
    education_items: [
      {
        degree: "Master Machine Learning for Data Science (MLSD)",
        school: "Université Paris-Cité",
        location: "Paris, France",
        year: "2022",
        project: "Predictive analysis and forecasting of pharmaceutical data at Sanofi"
      },
      {
        degree: "Master Big Data & Cloud Computing",
        school: "Université Ibn Tofail",
        location: "Kénitra, Morocco",
        year: "2021",
        project: "Optimization of MABED application with NLP and real-time Twitter stream analysis"
      },
      {
        degree: "Bachelor Mathematics & Computer Science",
        school: "Université Ibn Tofail",
        location: "Kénitra, Morocco",
        year: "2019",
        project: "Development of complete e-commerce web application with Laravel backend"
      }
    ],
    projects_items: [
      {
        title: "AI Chatbot & SaaS Platform",
        description: "Educational tutor chatbot (LangChain/LangGraph) integrating OpenAI, Claude and Gemini, plus a full-stack subscription SaaS with Stripe payments, SSO and an analytics dashboard"
      },
      {
        title: "Professional Websites",
        description: "Event-equipment rental platform with online booking & payment, and a dental-clinic site with appointment booking",
        links: ["sonorave-rentals.com", "banelhaqdental.com"]
      }
    ],
    skills_categories: {
      "Programming Languages": ["Python", "JavaScript", "R", "SQL", "Java", "C", "C++", "Shell"],
      "AI & ML": ["LangChain", "LangGraph", "OpenAI API", "Claude API", "TensorFlow", "PyTorch", "Scikit-learn", "NLP", "Deep Learning"],
      "Web Dev": ["React", "Node.js", "UI/UX Design", "Django", "Laravel", "REST APIs", "Stripe", "SSO"],
      "Cloud & Data": ["Azure", "Docker", "MLOps", "MLflow", "Spark", "Pandas", "NumPy", "Power BI", "Dataiku", "Git", "GitHub", "CI/CD", "Agile", "Scrum", "ETL"],
      "Databases": ["Supabase", "PostgreSQL", "SQL Server", "Oracle", "MongoDB"],
      "Tools": ["Postman", "API Integration", "Selenium", "Jupyter", "VS Code", "Monday.com", "Railway"]
    },
    languages_items: [
      { lang: "French", level: "Fluent", cert: "TCF DAP (2018), TCF TP (2020)" },
      { lang: "English", level: "Fluent", cert: "TOEIC 925/990, EFSET C2 Proficiency" },
      { lang: "Arabic", level: "Native", cert: "" }
    ]
  },
  fr: {
    title: "Data Scientist · Ingénieur Automatisation & Test IA/ML",
    profile: "Profil Professionnel",
    profileText1: "Data Scientist avec 4+ ans en modèles ML, de l'analyse prédictive (Python, Scikit-learn) à l'IA générative (LangChain/LangGraph, OpenAI, Claude, Gemini). MLOps (MLflow, Dataiku), évaluation IA/ML et pipelines à grande échelle. 20+ modèles en production (85%+), 100K+ points de données, validation de systèmes IA.",
    experience: "Expérience Professionnelle",
    education: "Formation",
    projects: "Projets Professionnels",
    skills: "Compétences Techniques",
    languages: "Langues",
    downloadPDF: "Télécharger PDF",
    portfolio: "Portfolio",
    current: "ACTUEL",
    experiences: [
      {
        title: "Ingénieur Automatisation & Test IA/ML",
        company: "Applause",
        location: "Télétravail",
        period: "Mai 2026 – Présent",
        points: [
          "Évaluation et validation des sorties IA/ML sur cycles de test end-to-end pour systèmes IA en production",
          "Automatisation Monday.com pour projets IA/ML : filtrage de données, configuration de tableaux et workflow",
          "Coordination d'équipes de test transverses (TTLs/DTs), point d'escalade sur cycles simultanés"
        ]
      },
      {
        title: "Ingénieur Automatisation & Full-Stack / Analyse de Données",
        company: "Edpulse",
        location: "Québec (Télétravail)",
        period: "Novembre 2024 – Mai 2026",
        points: [
          "Chatbots LangChain/LangGraph avec APIs IA (OpenAI, Claude, Gemini) en architecture microservices",
          "Système d'abonnement full-stack React/Node.js avec Stripe, SSO et CI/CD",
          "Automatisation (-60% tâches manuelles), dashboards KPI et ETL sur 10+ sources",
          "Monitoring Railway et APIs RESTful pour intégrations tierces"
        ]
      },
        {
          title: "Data Scientist",
          company: "Arhs Development",
          location: "Bruxelles, Belgique",
          period: "Juin 2023 – Juin 2024",
        points: [
          "Modèles ML Python/Scikit-learn pour finance et juridique (85%+ précision)",
          "Formation 15+ équipes sur Dataiku : preprocessing, modélisation et déploiement",
          "MLOps avec MLflow : versioning et monitoring de 20+ modèles en production"
        ]
      },
        {
          title: "Data Scientist Stagiaire",
          company: "Sanofi",
          location: "Gentilly, Île-de-France",
          period: "Avril 2022 – Novembre 2022",
        points: [
          "Modèles prédictifs ML pour marché pharma (Oncologie/Dupixent), 100K+ points de données",
          "ETL Python/SQL automatisé (-50% délais reporting)",
          "Dashboards Power BI pour suivi marché (30+ analystes)"
        ]
      },
      {
        title: "Data Scientist Freelance",
        company: "Khamsat",
        location: "Freelance",
        period: "Avril 2020 – Juin 2020",
        points: [
          "Scraping Python & GUI desktop pour e-commerce (10K+ produits/jour)",
          "App Django ML avec prédiction médicale, auth sécurisée et APIs REST"
        ]
      }
    ],
    education_items: [
      {
        degree: "Master Machine Learning pour la Science des Données (MLSD)",
        school: "Université Paris-Cité",
        location: "Paris, France",
        year: "2022",
        project: "Analyse prédictive et forecasting de données pharmaceutiques chez Sanofi"
      },
      {
        degree: "Master Big Data & Cloud Computing",
        school: "Université Ibn Tofail",
        location: "Kénitra, Maroc",
        year: "2021",
        project: "Optimisation de l'application MABED avec NLP et analyse de flux Twitter temps réel"
      },
      {
        degree: "Licence Mathématiques & Informatique",
        school: "Université Ibn Tofail",
        location: "Kénitra, Maroc",
        year: "2019",
        project: "Développement d'application web e-commerce complète avec backend Laravel"
      }
    ],
    projects_items: [
      {
        title: "Chatbot IA & Plateforme SaaS",
        description: "Chatbot tuteur (LangChain/LangGraph, OpenAI, Claude, Gemini) et SaaS full-stack (Stripe, SSO, analytics)"
      },
      {
        title: "Sites Web Professionnels",
        description: "Plateforme location matériel événementiel (réservation & paiement) et site cabinet dentaire (rendez-vous)",
        links: ["sonorave-rentals.com", "banelhaqdental.com"]
      }
    ],
    skills_categories: {
      "Langages de programmation": ["Python", "JavaScript", "R", "SQL", "Java", "C", "C++", "Shell"],
      "IA & ML": ["LangChain", "LangGraph", "OpenAI API", "Claude API", "TensorFlow", "PyTorch", "Scikit-learn", "NLP", "Deep Learning"],
      "Web Dev": ["React", "Node.js", "UI/UX Design", "Django", "Laravel", "REST APIs", "Stripe", "SSO"],
      "Cloud & Data": ["Azure", "Docker", "MLOps", "MLflow", "Spark", "Pandas", "NumPy", "Power BI", "Dataiku", "Git", "GitHub", "CI/CD", "Agile", "Scrum", "ETL"],
      "Bases de données": ["Supabase", "PostgreSQL", "SQL Server", "Oracle", "MongoDB"],
      "Outils": ["Postman", "Intégration API", "Selenium", "Jupyter", "VS Code", "Monday.com", "Railway"]
    },
    languages_items: [
      { lang: "Français", level: "Courant", cert: "TCF DAP (2018), TCF TP (2020)" },
      { lang: "Anglais", level: "Courant", cert: "TOEIC 925/990, EFSET C2 Proficiency" },
      { lang: "Arabe", level: "Langue maternelle", cert: "" }
    ]
  }
};
